import React, { useEffect, useRef, useState } from 'react'
import StarRounded from '@mui/icons-material/StarRounded'
import { categoryMeta, FallbackIcon, homeworkStatusLabel } from '../constants'
import StarRating, { ScoreCount } from './StarRating'
import { clampScore } from './scoreUtils'
import { isQuestActive, normalizeQuestStatus, questProgressLabel } from './questTypes'

function formatDeadline(deadline) {
  if (!deadline) return ''
  const date = new Date(deadline)
  if (Number.isNaN(date.getTime())) return String(deadline)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d} ${hh}:${mm}`
}

function formatDelta(delta) {
  if (delta == null || Number.isNaN(Number(delta))) return ''
  const n = Number(delta)
  if (Math.abs(n) < 0.05) return '持平'
  const abs = Number.isInteger(n) ? String(Math.abs(n)) : Math.abs(n).toFixed(1)
  return n > 0 ? `↑ ${abs}` : `↓ ${abs}`
}

function normalizedText(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, '')
    .trim()
}

/**
 * 关卡旁轻量浮层卡片
 */
function launchPayloadFromOption(item, option) {
  if (!option) return item
  return {
    ...item,
    assetId: option.assetId ?? item.assetId,
    releaseId: option.releaseId ?? item.releaseId,
    launchKind: option.launchKind || item.launchKind,
    flowName: option.flowName || item.flowName,
    templateId: option.templateId || item.templateId,
    agentCommand: option.agentCommand || item.agentCommand,
    description: option.description || item.description,
    title: item.title,
    selectedResourceTitle: option.title || item.title,
  }
}

export default function QuestDetailPopover({
  open,
  item,
  style,
  onClose,
  onLaunch,
  onOpenHomework,
  onOpenEvaluation,
  celebrateScore = false,
}) {
  const panelRef = useRef(null)
  const [descriptionExpanded, setDescriptionExpanded] = useState(false)
  const [selectedAssetId, setSelectedAssetId] = useState(null)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (e) => {
      const panel = panelRef.current
      if (!panel) return
      if (panel.contains(e.target)) return
      if (e.target.closest?.('.quest-node')) return
      onClose?.()
    }
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointer)
    }, 0)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('mousedown', onPointer)
    }
  }, [open, onClose])

  useEffect(() => {
    setDescriptionExpanded(false)
    const options = Array.isArray(item?.resourceOptions) ? item.resourceOptions : []
    const defaultId = options[0]?.assetId || item?.assetId || null
    setSelectedAssetId(defaultId)
  }, [item?.key, open, item?.assetId, item?.resourceOptions])

  if (!open || !item) return null

  const resourceOptions = Array.isArray(item.resourceOptions) ? item.resourceOptions : []
  const selectedOption = resourceOptions.find((opt) => String(opt.assetId) === String(selectedAssetId))
    || resourceOptions[0]
    || null
  const launchItem = launchPayloadFromOption(item, selectedOption)
  const meta = categoryMeta[item.assetType] || {
    label: item.assetType,
    Icon: FallbackIcon,
  }
  const Icon = meta.Icon
  const description = launchItem.description || item.description || item.content || '暂无关卡说明'
  const intro = typeof item.note === 'string' ? item.note.trim() : ''
  const introIsDuplicate = Boolean(intro) && (
    normalizedText(intro) === normalizedText(description)
    || normalizedText(intro) === normalizedText(item.content)
  )
  const teacherIntro = introIsDuplicate ? '' : intro
  const canLaunch = Boolean(launchItem.launchKind) && (
    resourceOptions.length <= 1 || Boolean(selectedOption)
  )
  const effectiveStatus = normalizeQuestStatus(
    item.progress?.status,
    item.assignmentStatus || item.progress?.assignmentStatus,
  )
  const statusKey = effectiveStatus || String(item.assignmentStatus || item.progress?.assignmentStatus || '')
  const statusText = questProgressLabel(item)
    || homeworkStatusLabel(item.assignmentStatus || item.progress?.assignmentStatus)
  const deadlineText = formatDeadline(item.deadline)
  const active = isQuestActive(item)
  const evalSummary = item.evaluationSummary || null
  const isEvaluated = effectiveStatus === 'evaluated'
  const isRejected = effectiveStatus === 'rejected'
  const scoreValue = clampScore(evalSummary?.score)
  const deltaText = isEvaluated ? formatDelta(evalSummary?.deltaFromPrevious) : ''
  const showEvalBlock = Boolean(evalSummary && (isEvaluated || isRejected))

  return (
    <div
      ref={panelRef}
      className="quest-popover"
      style={style}
      role="dialog"
      aria-label="关卡详情"
    >
      <div className="quest-popover-top">
        {statusText ? (
          <span className={`quest-popover-status status-${statusKey}`}>
            {statusText}
          </span>
        ) : null}
        {active ? <span className="quest-popover-current">当前应做</span> : null}
        {item.isHighlight ? (
          <span className="portal-highlight-star" title="推荐" aria-label="推荐">
            <StarRounded />
          </span>
        ) : null}
        <button
          type="button"
          className="quest-popover-close"
          onClick={onClose}
          aria-label="关闭"
        >
          ×
        </button>
      </div>
      <h3 className="quest-popover-title">
        <Icon className="quest-popover-title-icon" />
        <span>{item.title}</span>
      </h3>

      {showEvalBlock ? (
        <div className={`quest-popover-eval${isRejected ? ' is-rejected' : ''}`}>
          {isEvaluated && scoreValue != null ? (
            <div className="quest-popover-eval-row">
              <StarRating score={scoreValue} size="lg" animate={celebrateScore} />
              <span className="quest-popover-eval-score">
                <ScoreCount score={scoreValue} animate={celebrateScore} />
              </span>
              <span className="quest-popover-eval-label">
                {evalSummary.scoreSource === 'teacher' ? '教师评分' : '综合评分'}
              </span>
              {evalSummary.level ? (
                <span className="quest-popover-eval-level">{evalSummary.level}</span>
              ) : null}
              {deltaText ? (
                <span className={`quest-popover-eval-delta${Number(evalSummary.deltaFromPrevious) < 0 ? ' is-down' : ''}`}>
                  {deltaText}
                </span>
              ) : null}
            </div>
          ) : null}
          {evalSummary.comment ? (
            <p className="quest-popover-eval-comment">
              {isRejected ? '教师反馈：' : ''}{evalSummary.comment}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="quest-popover-description">
        <p className={`quest-popover-desc${descriptionExpanded ? ' is-expanded' : ''}`}>{description}</p>
        {normalizedText(description).length > 96 ? (
          <button
            type="button"
            className="quest-popover-expand"
            onClick={() => setDescriptionExpanded((value) => !value)}
          >
            {descriptionExpanded ? '收起说明' : '展开说明'}
          </button>
        ) : null}
      </div>
      {deadlineText ? (
        <p className="quest-popover-meta">截止：{deadlineText}</p>
      ) : null}

      {teacherIntro ? (
        <div className="quest-popover-intro">
          <span className="portal-card-intro-label">教师说明</span>
          <p className="quest-popover-intro-text">{teacherIntro}</p>
        </div>
      ) : null}

      {resourceOptions.length > 1 ? (
        <div className="quest-popover-resources">
          <span className="portal-card-intro-label">选择训练资源</span>
          <div className="quest-popover-resource-list" role="radiogroup" aria-label="候选训练资源">
            {resourceOptions.map((option) => {
              const checked = String(option.assetId) === String(selectedAssetId)
              return (
                <label
                  key={option.assetId}
                  className={`quest-popover-resource-item${checked ? ' is-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name={`quest-resource-${item.key}`}
                    checked={checked}
                    onChange={() => setSelectedAssetId(option.assetId)}
                  />
                  <span className="quest-popover-resource-title">{option.title || `资源 ${option.assetId}`}</span>
                </label>
              )
            })}
          </div>
        </div>
      ) : null}

      <div className="quest-popover-actions">
        {canLaunch ? (
          <button
            type="button"
            className="portal-card-action quest-popover-action"
            onClick={() => {
              onLaunch?.(launchItem)
              onClose?.()
            }}
          >
            开始训练
          </button>
        ) : null}
        {isEvaluated && evalSummary?.hasFullEvaluation ? (
          <button
            type="button"
            className={`portal-card-action quest-popover-action${canLaunch ? ' is-secondary' : ''}`}
            onClick={() => {
              onOpenEvaluation?.(item)
              onClose?.()
            }}
          >
            查看完整评价
          </button>
        ) : null}
        <button
          type="button"
          className="quest-popover-homework-link"
          onClick={() => {
            onOpenHomework?.(item)
            onClose?.()
          }}
        >
          {isRejected ? '查看作业并修改' : '查看作业'}
        </button>
      </div>
    </div>
  )
}
